import { useState } from 'react';
import { suggestCategory, type LocalDate, type NoteHistoryItem } from '@tahi/core';
import type { SpendingCategory, SpendingEntry } from '../../../data/repos/spending';
import { Button, Chip, Money } from '../../../ui';
import { copy } from '../copy';
import { dayHeading, spokenPeso } from '../format';

export interface TidyUpViewProps {
  entries: SpendingEntry[];
  categories: SpendingCategory[];
  noteHistory: NoteHistoryItem[];
  today: LocalDate;
  onCategorize(ids: string[], categoryId: string): void | Promise<void>;
}

export function TidyUpView({
  entries,
  categories,
  noteHistory,
  today,
  onCategorize,
}: TidyUpViewProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  if (entries.length === 0) return <p className="text-muted">{copy.tidyEmpty}</p>;

  const visible = categories.filter((c) => !c.hidden);
  const allowed = new Set(visible.map((c) => c.id));
  const allSelected = selected.size === entries.length;

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function apply(categoryId: string) {
    const ids = entries.filter((e) => selected.has(e.id)).map((e) => e.id);
    await onCategorize(ids, categoryId);
    setSelected(new Set());
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted">{copy.tidyIntro}</p>
      <Button
        variant="ghost"
        className="self-start"
        onClick={() => setSelected(allSelected ? new Set() : new Set(entries.map((e) => e.id)))}
      >
        {allSelected ? copy.selectNone : copy.selectAll}
      </Button>

      <ul aria-label={copy.tidyListLabel} className="divide-y divide-divider">
        {entries.map((e) => {
          const s = suggestCategory(e.note, noteHistory, { allowedCategoryIds: allowed });
          const suggested = s ? visible.find((c) => c.id === s.categoryId) : undefined;
          const primary = e.note ?? dayHeading(e.date, today);
          return (
            <li key={e.id} className="flex flex-col py-2">
              <label className="flex min-h-11 flex-1 items-center gap-3">
                <input
                  type="checkbox"
                  checked={selected.has(e.id)}
                  onChange={() => toggle(e.id)}
                  aria-label={`${spokenPeso(e.amountMinor)}, ${primary}`}
                  className="h-5 w-5 accent-[var(--accent)]"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-fg">{primary}</span>
                  {e.note ? (
                    <span className="text-xs text-muted">{dayHeading(e.date, today)}</span>
                  ) : null}
                </span>
                <Money minor={e.amountMinor} className="shrink-0" />
              </label>
              {suggested ? (
                <div className="pl-8">
                  <Chip
                    aria-label={copy.useSuggestionFor(suggested.name, primary)}
                    onClick={() => void onCategorize([e.id], suggested.id)}
                  >
                    {copy.suggested(suggested.name)}
                  </Chip>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {selected.size > 0 ? (
        <div
          role="group"
          aria-label={copy.moveTo(selected.size)}
          className="sticky bottom-24 flex flex-col gap-2 rounded-3xl border border-divider bg-surface-2 p-3"
        >
          <p className="text-sm text-fg">{copy.moveTo(selected.size)}</p>
          <div className="flex flex-wrap gap-2">
            {visible.map((c) => (
              <Chip key={c.id} onClick={() => void apply(c.id)}>
                {c.name}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
